import React, { useEffect, useState, useCallback, useMemo } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2 } from "lucide-react";

const WORKSPACES = ["all", "celestial_seed", "dubbi", "tribes", "school", "marketing", "construction", "fundraising", "media", "books", "courses", "website"];
const STATUSES = [{v:"todo", l:"To do"}, {v:"in_progress", l:"In progress"}, {v:"done", l:"Done"}];
const PRIORITIES = ["low", "medium", "high", "urgent"];
const PRIORITY_COLOR = { low: "bg-forest/10 text-forest", medium: "bg-forest/20 text-forest", high: "bg-terracotta/20 text-terracotta", urgent: "bg-terracotta text-bone" };

export default function Projects() {
  const [projects, setProjects] = useState([]);
  const [ws, setWs] = useState("all");
  const [active, setActive] = useState(null);
  const [tasks, setTasks] = useState([]);

  const load = useCallback(async () => {
    const { data } = await api.get("/projects", { params: { workspace: ws } });
    setProjects(data);
    if (!active && data.length) setActive(data[0]);
  }, [ws, active]);

  const loadTasks = useCallback(async (pid) => {
    if (!pid) return;
    const { data } = await api.get(`/projects/${pid}/tasks`);
    setTasks(data);
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (active) loadTasks(active.id); }, [active, loadTasks]);

  const grouped = useMemo(
    () => STATUSES.reduce((acc, s) => { acc[s.v] = tasks.filter(t => t.status === s.v); return acc; }, {}),
    [tasks]
  );

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="overline">Projects</div>
          <h1 className="font-serif text-4xl mt-1">Workspaces</h1>
        </div>
        <div className="flex gap-3">
          <Select value={ws} onValueChange={setWs}>
            <SelectTrigger className="w-48" data-testid="projects-ws-filter"><SelectValue /></SelectTrigger>
            <SelectContent>{WORKSPACES.map(w => <SelectItem key={w} value={w}>{w.replace("_"," ")}</SelectItem>)}</SelectContent>
          </Select>
          <NewProject onCreated={load} />
        </div>
      </div>

      <div className="grid lg:grid-cols-[320px_1fr] gap-6">
        <div className="bg-admin-surface border border-line rounded-xl p-3 space-y-1 max-h-[70vh] overflow-auto">
          {projects.map(p => (
            <button key={p.id} onClick={() => setActive(p)} data-testid={`project-item-${p.id}`}
              className={`w-full text-left px-3 py-3 rounded-md transition-colors ${active?.id === p.id ? "bg-forest text-bone" : "hover:bg-bone/70"}`}>
              <div className="font-medium">{p.name}</div>
              <div className={`text-xs mt-1 ${active?.id === p.id ? "text-bone/70" : "text-ink-soft"}`}>{p.workspace.replace("_"," ")}</div>
            </button>
          ))}
          {!projects.length && <div className="text-ink-soft text-sm p-4">No projects.</div>}
        </div>

        {active ? (
          <div className="space-y-4">
            <div className="bg-admin-surface border border-line rounded-xl p-6 flex items-center justify-between">
              <div>
                <div className="font-serif text-2xl">{active.name}</div>
                <div className="text-ink-soft text-sm mt-1">{active.description}</div>
              </div>
              <NewTask projectId={active.id} onCreated={() => loadTasks(active.id)} />
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              {STATUSES.map(s => (
                <div key={s.v} className="bg-admin-surface border border-line rounded-xl p-4 min-h-[300px]">
                  <div className="overline mb-3 flex items-center justify-between">{s.l} <span className="text-ink-soft">{grouped[s.v].length}</span></div>
                  <div className="space-y-2">
                    {grouped[s.v].map(t => (
                      <div key={t.id} className="rounded-md border border-line p-3 bg-bone/40" data-testid={`task-${t.id}`}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="font-medium text-ink text-sm">{t.title}</div>
                          <button onClick={async () => { await api.delete(`/tasks/${t.id}`); loadTasks(active.id); }} className="text-ink-soft hover:text-terracotta"><Trash2 className="h-3 w-3" /></button>
                        </div>
                        <div className="flex items-center gap-2 mt-2">
                          <Badge className={`${PRIORITY_COLOR[t.priority]} text-xs`}>{t.priority}</Badge>
                          <Select value={t.status} onValueChange={async (v) => { await api.patch(`/tasks/${t.id}`, { status: v }); loadTasks(active.id); }}>
                            <SelectTrigger className="h-7 text-xs border-line"><SelectValue /></SelectTrigger>
                            <SelectContent>{STATUSES.map(ss => <SelectItem key={ss.v} value={ss.v}>{ss.l}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : <div className="text-ink-soft">Select a project.</div>}
      </div>
    </div>
  );
}

function NewProject({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", workspace: "celestial_seed", description: "", owner: "" });
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/projects", f); toast.success("Project created"); setOpen(false); onCreated(); setF({ name: "", workspace: "celestial_seed", description: "", owner: "" }); }
    catch (e) { toast.error("Failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><button className="pill pill-primary text-sm" data-testid="new-project-btn"><Plus className="h-4 w-4" /> New Project</button></DialogTrigger>
      <DialogContent className="bg-bone">
        <DialogHeader><DialogTitle className="font-serif text-2xl">New Project</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div><Label className="overline">Name</Label><Input required data-testid="np-name" value={f.name} onChange={e=>setF({...f, name: e.target.value})} /></div>
          <div><Label className="overline">Workspace</Label>
            <Select value={f.workspace} onValueChange={v => setF({...f, workspace: v})}>
              <SelectTrigger data-testid="np-workspace"><SelectValue /></SelectTrigger>
              <SelectContent>{WORKSPACES.filter(w => w !== "all").map(w => <SelectItem key={w} value={w}>{w.replace("_"," ")}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="overline">Owner</Label><Input data-testid="np-owner" value={f.owner} onChange={e=>setF({...f, owner: e.target.value})} /></div>
          <div><Label className="overline">Description</Label><Textarea rows={3} data-testid="np-description" value={f.description} onChange={e=>setF({...f, description: e.target.value})} /></div>
          <button type="submit" data-testid="np-submit" className="pill pill-primary">Save</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function NewTask({ projectId, onCreated }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ title: "", description: "", priority: "medium", status: "todo", deadline: "" });
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post(`/projects/${projectId}/tasks`, f); toast.success("Task added"); setOpen(false); onCreated(); setF({ title: "", description: "", priority: "medium", status: "todo", deadline: "" }); }
    catch (e) { toast.error("Failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><button className="pill pill-primary text-sm" data-testid="new-task-btn"><Plus className="h-4 w-4" /> New Task</button></DialogTrigger>
      <DialogContent className="bg-bone">
        <DialogHeader><DialogTitle className="font-serif text-2xl">New Task</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div><Label className="overline">Title</Label><Input required data-testid="nt-title" value={f.title} onChange={e=>setF({...f, title: e.target.value})} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="overline">Priority</Label>
              <Select value={f.priority} onValueChange={v => setF({...f, priority: v})}>
                <SelectTrigger data-testid="nt-priority"><SelectValue /></SelectTrigger>
                <SelectContent>{PRIORITIES.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="overline">Status</Label>
              <Select value={f.status} onValueChange={v => setF({...f, status: v})}>
                <SelectTrigger data-testid="nt-status"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUSES.map(s => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div><Label className="overline">Deadline</Label><Input type="date" data-testid="nt-deadline" value={f.deadline} onChange={e=>setF({...f, deadline: e.target.value})} /></div>
          <button type="submit" data-testid="nt-submit" className="pill pill-primary">Save</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
